import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, DestroyRef, effect, inject, input, model, output, signal, untracked } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize } from 'rxjs';

import { PERIODOS_PRORROGACAO, PrestadorProrrogacaoRequest } from '@/core/api/sicom-api/registro/prestador/prestador-situacao.model';
import { PrestadorSituacaoService } from '@/core/api/sicom-api/registro/prestador/prestador-situacao.service';
import { NotificacoesService } from '@/core/services/notificacoes.service';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { Select } from 'primeng/select';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
const PERIODO_DEFAULT = 120;

@Component({
    selector: 'app-prorrogar-prazo',
    standalone: true,
    imports: [DialogoPadraoDirective, ReactiveFormsModule, Dialog, Select, Button],
    templateUrl: './prorrogar-prazo.html'
})
export class ProrrogarPrazo {
    readonly visible = model<boolean>(false);
    readonly pessoa = input<Pessoa | null>(null);
    readonly concluido = output<void>();

    private readonly destroyRef = inject(DestroyRef);
    private readonly fb = inject(FormBuilder);
    private readonly prestadorSituacaoService = inject(PrestadorSituacaoService);
    private readonly notificacoesService = inject(NotificacoesService);

    readonly salvando = signal(false);
    readonly opcoesPeriodo = PERIODOS_PRORROGACAO.map((periodo) => ({ label: `${periodo} dias`, value: periodo }));

    readonly form: FormGroup = this.fb.group({
        periodo: [PERIODO_DEFAULT as number | null, Validators.required]
    });

    constructor() {
        // Reseta o formulário sempre que o diálogo é aberto.
        effect(() => {
            if (this.visible()) {
                untracked(() => this.resetarFormulario());
            }
        });
    }

    fechar(): void {
        this.visible.set(false);
    }

    salvar(): void {
        if (this.form.invalid || this.salvando()) {
            this.form.markAllAsTouched();
            return;
        }

        const pessoa = this.pessoa();
        const periodo = this.form.getRawValue().periodo;
        if (!pessoa || periodo == null) {
            return;
        }

        const request: PrestadorProrrogacaoRequest = { id: pessoa.id, uf: pessoa.uf, periodo };

        this.salvando.set(true);
        this.prestadorSituacaoService
            .prorrogarPrazo(request)
            .pipe(
                finalize(() => this.salvando.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    this.notificacoesService.notificar({
                        severity: 'success',
                        summary: 'Prazo prorrogado',
                        detail: 'Prazo da inscrição provisória pendente prorrogado com sucesso.'
                    });
                    this.visible.set(false);
                    this.concluido.emit();
                },
                error: () => {}
            });
    }

    private resetarFormulario(): void {
        this.form.reset({ periodo: PERIODO_DEFAULT });
        this.salvando.set(false);
    }
}
